import {
  AST,
  BlockNode,
  InlineNode,
  DocumentNode,
  HeadingNode,
  ParagraphNode,
  ListNode,
  ListItemNode,
  CodeBlockNode,
  BlockquoteNode,
  TableNode,
  ModuleBlockNode,
  AlignmentBlockNode,
  FloatBlockNode,
  TextNode,
  BoldNode,
  ItalicNode,
  BoldItalicNode,
  CodeNode,
  LinkNode,
  ModuleInlineNode,
  LatexNode,
  FootnoteNode,
  EndnoteNode,
  CitationNode,
} from '../markdown/nodes';
import {
  Book,
  Chapter,
  Section,
  ContentElement,
  InlineElement,
  ParagraphElement,
  HeadingElement,
  ListElement,
  ListItemElement,
  BlockquoteElement,
  CodeBlockElement,
  TableElement,
  ModuleElement,
  FloatElement,
  HrElement,
  TextInline,
  BoldInline,
  ItalicInline,
  CodeInline,
  LinkInline,
  ModuleInline,
  LatexInline,
  FootnoteInline,
  EndnoteInline,
  CitationInline,
} from './types';
import { parseNotes, NotesCollection } from './notes';
import { parseMarkdown } from '../markdown';

export interface ParsedBook {
  book: Book;
  notes: NotesCollection;
}

export class BookParser {
  private chapterNumber = 0;
  private currentChapter: Chapter | null = null;
  private currentSection: Section | null = null;
  private notes: NotesCollection = new NotesCollection();
  private footnoteCounter = 0;
  private endnoteCounter = 0;
  private citationCounter = 0;
  private citationNumbers = new Map<string, number>();

  parse(markdown: string, title = 'Untitled Book'): ParsedBook {
    const ast = parseMarkdown(markdown);

    this.chapterNumber = 0;
    this.currentChapter = null;
    this.currentSection = null;
    this.notes = new NotesCollection();
    this.footnoteCounter = 0;
    this.endnoteCounter = 0;
    this.citationCounter = 0;
    this.citationNumbers = new Map();

    const chapters: Chapter[] = [];
    let bookTitle = title;

    for (const node of ast.document.children) {
      if (node.type === 'heading') {
        const headingNode = node;

        if (headingNode.level === 1) {
          if (this.currentChapter) {
            if (this.currentSection) {
              this.currentChapter.sections.push(this.currentSection);
              this.currentSection = null;
            }
            chapters.push(this.currentChapter);
          }

          this.chapterNumber++;
          this.currentChapter = {
            number: this.chapterNumber,
            title: this.getInlineText(headingNode.text),
            sections: [],
          };

          if (this.chapterNumber === 1 && !title) {
            bookTitle = this.currentChapter.title;
          }

          this.currentSection = null;
        } else if (headingNode.level === 2 && this.currentChapter) {
          if (this.currentSection) {
            this.currentChapter.sections.push(this.currentSection);
          }

          this.currentSection = {
            level: headingNode.level,
            title: this.getInlineText(headingNode.text),
            content: [],
          };
        }
      } else if (this.currentChapter) {
        const element = this.convertBlockNode(node);

        if (this.currentSection) {
          this.currentSection.content.push(element);
        } else {
          if (!this.currentSection && this.currentChapter.sections.length === 0) {
            this.currentSection = {
              level: 0,
              title: '',
              content: [],
            };
          }
          if (this.currentSection) {
            this.currentSection.content.push(element);
          }
        }
      }
    }

    if (this.currentChapter) {
      if (this.currentSection) {
        this.currentChapter.sections.push(this.currentSection);
      }
      chapters.push(this.currentChapter);
    }

    this.notes = parseNotes(markdown);

    return {
      book: {
        title: bookTitle,
        chapters,
      },
      notes: this.notes,
    };
  }

  private convertBlockNode(node: BlockNode): ContentElement {
    switch (node.type) {
      case 'paragraph': {
        const pNode = node;
        return {
          type: 'paragraph',
          inlines: this.convertInlineNodes(pNode.children),
        } as ParagraphElement;
      }

      case 'heading': {
        const hNode = node;
        return {
          type: 'heading',
          level: hNode.level,
          text: this.getInlineText(hNode.text),
        } as HeadingElement;
      }

      case 'list': {
        const lNode = node;
        return {
          type: 'list',
          ordered: lNode.ordered,
          items: lNode.children.map((item) => this.convertListItem(item)),
        } as ListElement;
      }

      case 'blockquote': {
        const bNode = node;
        const child = bNode.children[0];
        if (child?.type === 'paragraph') {
          const pNode = child;
          return {
            type: 'blockquote',
            content: this.getInlineText(pNode.children),
          } as BlockquoteElement;
        }
        return { type: 'blockquote', content: '' } as BlockquoteElement;
      }

      case 'codeBlock': {
        const cNode = node;
        return {
          type: 'codeBlock',
          language: cNode.language,
          content: cNode.content,
        } as CodeBlockElement;
      }

      case 'table': {
        const tNode = node;
        const headerCells = tNode.headerCells?.map((row) => this.convertInlineNodes(row)) || [];
        const cells =
          tNode.cells?.map((row) => row.map((cell) => this.convertInlineNodes(cell))) || [];

        return {
          type: 'table',
          headers: tNode.headers,
          rows: tNode.rows,
          headerCells,
          cells,
        } as TableElement;
      }

      case 'module': {
        const mNode = node;
        return {
          type: 'module',
          moduleName: mNode.moduleName,
          content: mNode.content,
          attrs: mNode.attrs,
        } as ModuleElement;
      }

      case 'alignment': {
        const aNode = node;
        // Get the first child (usually a paragraph) and set its alignment
        const firstChild = aNode.children[0];
        if (firstChild?.type === 'paragraph') {
          const pNode = firstChild;
          return {
            type: 'paragraph',
            inlines: this.convertInlineNodes(pNode.children),
            align: aNode.align,
          } as ParagraphElement;
        }
        // If not a paragraph, wrap in paragraph with alignment
        return {
          type: 'paragraph',
          inlines: [],
          align: aNode.align,
        } as ParagraphElement;
      }

      case 'float': {
        const fNode = node;
        const content = fNode.content.map((c) => this.convertBlockNode(c));
        return {
          type: 'float',
          align: fNode.align,
          width: fNode.width,
          content,
        } as FloatElement;
      }

      case 'hr':
        return { type: 'hr' } as HrElement;

      default:
        return { type: 'paragraph', inlines: [] } as ParagraphElement;
    }
  }

  private convertInlineNodes(nodes: InlineNode[]): InlineElement[] {
    return nodes
      .map((node) => this.convertInlineNode(node))
      .filter((n): n is InlineElement => n !== null);
  }

  private convertInlineNode(node: InlineNode): InlineElement | null {
    switch (node.type) {
      case 'text': {
        const tNode = node;
        return { type: 'text', content: tNode.content } as TextInline;
      }

      case 'bold': {
        const bNode = node;
        return {
          type: 'bold',
          children: this.convertInlineNodes(bNode.children),
        } as BoldInline;
      }

      case 'italic': {
        const iNode = node;
        return {
          type: 'italic',
          children: this.convertInlineNodes(iNode.children),
        } as ItalicInline;
      }

      case 'boldItalic': {
        const biNode = node;
        return {
          type: 'bold',
          children: this.convertInlineNodes(biNode.children),
        } as BoldInline;
      }

      case 'code': {
        const cNode = node;
        return { type: 'code', content: cNode.content } as CodeInline;
      }

      case 'link': {
        const lNode = node;
        return {
          type: 'link',
          href: lNode.href,
          title: lNode.title,
          children: this.convertInlineNodes(lNode.children),
        } as LinkInline;
      }

      case 'module': {
        const mNode = node;
        return {
          type: 'module',
          moduleName: mNode.moduleName,
          content: mNode.content,
          attrs: mNode.attrs,
        } as ModuleInline;
      }

      case 'latex': {
        const lNode = node;
        return { type: 'latex', content: lNode.content, display: lNode.display } as LatexInline;
      }

      case 'footnote': {
        const fNode = node;
        return {
          type: 'footnote',
          content: fNode.content,
          number: ++this.footnoteCounter,
        } as FootnoteInline;
      }

      case 'endnote': {
        const eNode = node;
        return {
          type: 'endnote',
          content: eNode.content,
          number: ++this.endnoteCounter,
        } as EndnoteInline;
      }

      case 'citation': {
        const cNode = node;
        let num = this.citationNumbers.get(cNode.id);
        if (num === undefined) {
          num = ++this.citationCounter;
          this.citationNumbers.set(cNode.id, num);
        }
        return {
          type: 'citation',
          id: cNode.id,
          content: cNode.content,
          number: num,
        } as CitationInline;
      }

      default:
        return null;
    }
  }

  private getInlineText(nodes: InlineNode[]): string {
    return nodes
      .map((node) => {
        switch (node.type) {
          case 'text':
            return (node).content;
          case 'bold':
          case 'italic':
          case 'boldItalic':
            return this.getInlineText((node as BoldNode).children);
          case 'code':
            return (node).content;
          case 'link':
            return this.getInlineText((node).children);
          case 'latex':
            return `$${(node).content}$`;
          case 'module':
            return `<${(node).moduleName}>...</${(node).moduleName}>`;
          case 'footnote':
          case 'endnote':
            return '';
          case 'citation':
            return '';
          default:
            return '';
        }
      })
      .join('');
  }

  private convertListItem(item: ListItemNode): ListItemElement {
    const inlines: InlineElement[] = [];
    const children: ListElement[] = [];

    for (const child of item.children) {
      if (child.type === 'paragraph') {
        const pNode = child;
        inlines.push(...this.convertInlineNodes(pNode.children));
      } else if (child.type === 'list') {
        const lNode = child;
        children.push({
          type: 'list',
          ordered: lNode.ordered,
          items: lNode.children.map((nestedItem) => this.convertListItem(nestedItem)),
        });
      }
    }

    return {
      type: 'listItem',
      inlines,
      children: children.length > 0 ? children : undefined,
      indent: item.indent,
    };
  }
}

export function parseBook(markdown: string, title?: string): ParsedBook {
  const parser = new BookParser();
  return parser.parse(markdown, title);
}
